import {
  ChangeEvent,
  FC,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Typography } from "@mui/material";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { format } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

type MilestoneTimeValue = string | Date | null | undefined;

type MilestoneClockPickerProps = {
  value?: MilestoneTimeValue;
  onChange: (value: string) => void;
  timezone?: string;
  /** Shows the read-only "Milestone date" label above the clock fields. Defaults to false. */
  showDate?: boolean;
  /** Shows the Second field alongside Hour/Minute/Period. Defaults to false. */
  showSeconds?: boolean;
};

type PeriodType = "AM" | "PM";

const MINUTES_IN_HOUR = 60;
const SECONDS_IN_MINUTE = 60;

const pad = (num: number) => String(num).padStart(2, "0");

// Wraps `value` into [0, mod) without ever carrying into the next field —
// used for minute/second, which must never bump the hour or date.
const wrapValue = (value: number, mod: number) => ((value % mod) + mod) % mod;

const to12HourDisplay = (
  hour24: number
): { hour: number; period: PeriodType } => {
  const period: PeriodType = hour24 >= 12 ? "PM" : "AM";
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour, period };
};

const getBaseDate = (value?: MilestoneTimeValue) => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return new Date(value);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return new Date();
};

// A "wall clock" value: a plain Date used only as a container for the
// year/month/day/hour/minute/second the milestone shows in the hospital's
// timezone. It is never treated as a real instant on its own — its fields
// are read/written directly, so the calendar date lives on this same Date
// object. Hour changes use standard Date rollover, so crossing midnight
// naturally updates this date; minute/second changes wrap within their own
// field only and never touch the hour or date.
const toWallClock = (value: MilestoneTimeValue, tz?: string): Date => {
  const instant = getBaseDate(value);
  if (!tz) {
    return new Date(instant);
  }
  const year = parseInt(formatInTimeZone(instant, tz, "yyyy"), 10);
  const month = parseInt(formatInTimeZone(instant, tz, "MM"), 10);
  const day = parseInt(formatInTimeZone(instant, tz, "dd"), 10);
  const hour = parseInt(formatInTimeZone(instant, tz, "HH"), 10);
  const minute = parseInt(formatInTimeZone(instant, tz, "mm"), 10);
  const second = parseInt(formatInTimeZone(instant, tz, "ss"), 10);
  return new Date(year, month - 1, day, hour, minute, second);
};

const fromWallClock = (wallClock: Date, tz?: string): string => {
  if (!tz) {
    return wallClock.toISOString();
  }
  const localStr =
    `${wallClock.getFullYear()}-${pad(wallClock.getMonth() + 1)}-${pad(
      wallClock.getDate()
    )}` +
    `T${pad(wallClock.getHours())}:${pad(wallClock.getMinutes())}:${pad(
      wallClock.getSeconds()
    )}`;
  return fromZonedTime(localStr, tz).toISOString();
};

type TimeBoxProps = {
  value: string;
  label: string;
  onIncrease: () => void;
  onDecrease: () => void;
  /** When provided (with min/max), the value box becomes an editable numeric field. */
  editable?: {
    min: number;
    max: number;
    onCommit: (nextValue: number) => void;
  };
};

const TimeBox = ({
  value,
  onIncrease,
  onDecrease,
  label,
  editable,
}: TimeBoxProps) => {
  const [draft, setDraft] = useState(value);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraft(value);
    }
  }, [value]);

  const commitDraft = () => {
    isEditingRef.current = false;
    if (!editable) return;

    if (draft === "") {
      setDraft(value);
      return;
    }

    const parsed = Math.min(
      editable.max,
      Math.max(editable.min, parseInt(draft, 10))
    );
    setDraft(pad(parsed));
    editable.onCommit(parsed);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          fontWeight: 500,
          mb: 2,
          color: "custom.foreground.primary",
        }}
      >
        {label}
      </Typography>

      <IconButton onClick={onIncrease} sx={{ mb: 1 }}>
        <KeyboardArrowUpRoundedIcon
          sx={{ fontSize: 38, color: "custom.foreground.brand" }}
        />
      </IconButton>

      <Box
        sx={{
          width: "100%",
          maxWidth: 80,
          minWidth: 0,
          height: 50,
          borderRadius: "10px",
          backgroundColor: "rgba(217, 203, 255, 0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          boxSizing: "border-box",
        }}
      >
        {editable ? (
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={draft}
            aria-label={`${label} input`}
            onFocus={(e: FocusEvent<HTMLInputElement>) => {
              isEditingRef.current = true;
              e.target.select();
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
              if (digits !== "" && editable) {
                const parsed = parseInt(digits, 10);
                // Reject a value once it exceeds the max (e.g. hour > 12, minute
                // > 59) — no further digit could ever make it valid.
                if (parsed > editable.max) return;
                // A single leading digit (e.g. "0") is a valid stepping stone
                // toward "01"-"09", so only enforce the min once two digits are
                // in — a complete entry like "00" can't become valid by typing more.
                if (digits.length === 2 && parsed < editable.min) {
                  setDraft(pad(editable.min));
                  return;
                }
              }
              setDraft(digits);
            }}
            onBlur={commitDraft}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            sx={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              textAlign: "center",
              fontFamily: "inherit",
              fontSize: "28px",
              lineHeight: "100%",
              letterSpacing: "0.37px",
              fontWeight: 700,
              color: "custom.background.brand",
              MozAppearance: "textfield",
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "28px",
              lineHeight: "100%",
              letterSpacing: "0.37px",
              fontWeight: 700,
              color: "custom.background.brand",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>

      <IconButton onClick={onDecrease} sx={{ mt: 1 }}>
        <KeyboardArrowDownRoundedIcon
          sx={{ fontSize: 38, color: "custom.foreground.brand" }}
        />
      </IconButton>
    </Box>
  );
};

type DateSummaryProps = {
  date: Date;
};

// Read-only calendar date, driven directly by wallClock — there is no
// separate date state and no manual date control. Hour arrows can roll
// wallClock's date forward or backward via standard Date rollover (see
// stepHours below), so this label reflects that automatically on re-render.
const DateSummary = ({ date }: DateSummaryProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        mb: 3,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: 14,
          fontWeight: 600,
          alignSelf: "flex-start",
          color: "custom.foreground.primary",
        }}
      >
        {`Milestone date: ${format(date, "dd MMM")}`}
      </Typography>
    </Box>
  );
};

const MilestoneClockPicker: FC<MilestoneClockPickerProps> = ({
  value,
  onChange,
  timezone,
  showDate = false,
  showSeconds = false,
}) => {
  const [wallClock, setWallClock] = useState<Date>(() =>
    toWallClock(value, timezone)
  );

  const lastExternalValueRef = useRef<MilestoneTimeValue>(value);

  useEffect(() => {
    if (value === lastExternalValueRef.current) return;

    lastExternalValueRef.current = value;
    setWallClock(toWallClock(value, timezone));
  }, [value, timezone]);

  const commit = (nextWallClock: Date) => {
    setWallClock(nextWallClock);

    const nextIso = fromWallClock(nextWallClock, timezone);
    lastExternalValueRef.current = nextIso;
    onChange(nextIso);
  };

  // Standard Date rollover: an hour that overflows past 23 (or below 0)
  // carries into the calendar day, so crossing midnight here intentionally
  // moves wallClock's date forward or backward — the read-only date label
  // above re-renders from wallClock and reflects that automatically.
  const stepHours = (delta: number) => {
    const next = new Date(wallClock);
    next.setHours(next.getHours() + delta);
    commit(next);
  };

  // Minute/second wrap within their own field only and never carry into
  // the hour or date.
  const stepMinutes = (delta: number) => {
    const next = new Date(wallClock);
    next.setMinutes(wrapValue(next.getMinutes() + delta, MINUTES_IN_HOUR));
    commit(next);
  };

  const stepSeconds = (delta: number) => {
    const next = new Date(wallClock);
    next.setSeconds(wrapValue(next.getSeconds() + delta, SECONDS_IN_MINUTE));
    commit(next);
  };

  // Typing a specific hour keeps the current AM/PM period, so hour24 is
  // always within [0, 23] and never carries into the date.
  const commitHour12 = (hour12: number) => {
    const hour24 = (hour12 % 12) + (period === "PM" ? 12 : 0);
    const next = new Date(wallClock);
    next.setHours(hour24);
    commit(next);
  };

  const commitMinute = (nextMinute: number) => {
    const next = new Date(wallClock);
    next.setMinutes(nextMinute);
    commit(next);
  };

  const commitSecond = (nextSecond: number) => {
    const next = new Date(wallClock);
    next.setSeconds(nextSecond);
    commit(next);
  };

  const { hour, period } = to12HourDisplay(wallClock.getHours());
  const minute = wallClock.getMinutes();
  const second = wallClock.getSeconds();

  // AM hours are 0-11 (+12 -> 12-23) and PM hours are 12-23 (-12 -> 0-11),
  // so the result is always a valid 0-23 hour with no carry into the date.
  const togglePeriod = () => {
    const next = new Date(wallClock);
    const nextHour =
      period === "AM" ? next.getHours() + 12 : next.getHours() - 12;
    next.setHours(nextHour);
    commit(next);
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 1, sm: 2 },
        py: 2,
        boxSizing: "border-box",
      }}
    >
      {showDate && <DateSummary date={wallClock} />}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <TimeBox
          label="Hour"
          value={pad(hour)}
          onIncrease={() => stepHours(1)}
          onDecrease={() => stepHours(-1)}
          editable={{
            min: 1,
            max: 12,
            onCommit: commitHour12,
          }}
        />

        <Typography
          sx={{
            fontSize: "30px",
            fontWeight: 700,
            lineHeight: "100%",
            letterSpacing: "0.37px",
            color: "custom.background.brand",
            mt: 3,
          }}
        >
          :
        </Typography>

        <TimeBox
          label="Minute"
          value={pad(minute)}
          onIncrease={() => stepMinutes(1)}
          onDecrease={() => stepMinutes(-1)}
          editable={{
            min: 0,
            max: 59,
            onCommit: commitMinute,
          }}
        />

        {showSeconds && (
          <>
            <Typography
              sx={{
                fontSize: "30px",
                fontWeight: 700,
                lineHeight: "100%",
                letterSpacing: "0.37px",
                color: "custom.background.brand",
                mt: 3,
              }}
            >
              :
            </Typography>

            <TimeBox
              label="Second"
              value={pad(second)}
              onIncrease={() => stepSeconds(1)}
              onDecrease={() => stepSeconds(-1)}
              editable={{
                min: 0,
                max: 59,
                onCommit: commitSecond,
              }}
            />
          </>
        )}

        <TimeBox
          label="Period"
          value={period}
          onIncrease={togglePeriod}
          onDecrease={togglePeriod}
        />
      </Box>
    </Box>
  );
};

export default MilestoneClockPicker;
